import { publicRequest } from "../../../requestMeth";
import {
  getNotificationsStart,
  getNotificationsSuccess,
  getNotificationsFailure,
  updateNotificationReadStart,
  updateNotificationReadSuccess,
  updateNotificationReadFailure,
  resetNotificationsState,
} from "../../reducers/notifications/notificationsRedux";

export const getNotifications = async (dispatch, token, filters = {}) => {
  dispatch(getNotificationsStart());

  try {
    const {
      include = ["count", "items"],
      // Default global feed excludes AI recommendations while this temporary switch is active.
      sources = ["event", "report", "automatic_report"],
      page = 1,
      limit = 20,
      date = null,
      dateFrom = null,
      dateTo = null,
      type = null,
      device_ids = [],
    } = filters;

    const normalizedInclude = Array.isArray(include)
      ? include.join(",")
      : include;

    const normalizedSources = Array.isArray(sources)
      ? sources.join(",")
      : sources;

    const normalizedDeviceIds = Array.isArray(device_ids)
      ? device_ids
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id))
      : [];

    const res = await publicRequest.get("/notifications", {
      headers: {
        token: `Bearer ${token}`,
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
      },
      params: {
        include: normalizedInclude,
        sources: normalizedSources,
        page: Number(page) || 1,
        limit: Number(limit) || 20,
        ...(date && { date }),
        ...(dateFrom && { dateFrom }),
        ...(dateTo && { dateTo }),
        ...(type && { type }),
        ...(normalizedDeviceIds.length > 0
          ? { device_ids: normalizedDeviceIds.join(",") }
          : {}),
        _t: Date.now(),
      },
    });

    dispatch(getNotificationsSuccess(res.data));
    return res.data;
  } catch (error) {
    dispatch(getNotificationsFailure());
    throw error;
  }
};

export const getCommunicationNotifications = async (
  token,
  filters = {},
) => {
  const {
    include = ["count", "items"],
    page = 1,
    limit = 1000,
  } = filters;

  const normalizedInclude = Array.isArray(include)
    ? include.join(",")
    : include;

  const res = await publicRequest.get("/notifications", {
    headers: {
      token: `Bearer ${token}`,
      "Cache-Control": "no-cache, no-store, must-revalidate",
      Pragma: "no-cache",
      Expires: "0",
    },
    params: {
      include: normalizedInclude,
      sources: "communication",
      page: Number(page) || 1,
      limit: Number(limit) || 1000,
      _t: Date.now(),
    },
  });

  return res.data;
};

export const markCommunicationNotificationRead = async (
  token,
  id,
  is_read = true,
) => {
  const normalizedId = Number(id);

  if (!Number.isFinite(normalizedId)) {
    throw new Error("Invalid communication notification id");
  }

  const res = await publicRequest.patch(
    `/notifications/communication/${normalizedId}/read`,
    { is_read: !!is_read },
    {
      headers: {
        token: `Bearer ${token}`,
      },
    },
  );

  return res.data;
};

export const updateNotificationReadStatusUnified = async (
  dispatch,
  { source, id, is_read = true },
  token
) => {
  dispatch(updateNotificationReadStart());

  try {
    const normalizedId = Number(id);
    const normalizedIsRead = !!is_read;
    const config = {
      headers: { token: `Bearer ${token}` },
    };

    let res;

    if (source === "event") {
      res = await publicRequest.put(
        "/alert-events/bulk",
        {
          ids: [normalizedId],
          is_read: normalizedIsRead,
        },
        config
      );
    } else if (source === "report") {
      res = await publicRequest.patch(
        `/notifications/report/${normalizedId}/read`,
        { is_read: normalizedIsRead },
        config
      );
    } else if (source === "automatic_report") {
      res = await publicRequest.patch(
        `/notifications/automatic_report/${normalizedId}/read`,
        { is_read: normalizedIsRead },
        config
      );
    } else if (source === "communication") {
      res = await publicRequest.patch(
        `/notifications/communication/${normalizedId}/read`,
        { is_read: normalizedIsRead },
        config
      );
    } else if (source === "recommendation") {
      res = await publicRequest.patch(
        `/notifications/recommendation/${normalizedId}/read`,
        { is_read: normalizedIsRead },
        config
      );
    } else {
      throw new Error(`Unsupported notification source: ${source}`);
    }

    dispatch(
      updateNotificationReadSuccess({
        source,
        id: normalizedId,
        is_read: normalizedIsRead,
        serverResponse: res.data,
      })
    );

    return res.data;
  } catch (error) {
    dispatch(updateNotificationReadFailure());
    throw error;
  }
};

export const clearNotificationsState = (dispatch) => {
  dispatch(resetNotificationsState());
};
